/**
 * BrowserNotificationService - Handles Web Notifications API for BackOffice
 *
 * Manages permission requests and fires browser notifications
 * for important admin events like new bookings, chats, and robots.
 */

export type NotificationPermission = "granted" | "denied" | "default";

export type NotificationOptions = {
  title: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: any;
};

class BrowserNotificationService {
  private isSupported: boolean;

  constructor() {
    this.isSupported = typeof window !== "undefined" && "Notification" in window;
  }

  /**
   * Check if Web Notifications API is supported
   */
  isNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported) {
      return "denied";
    }
    return Notification.permission as NotificationPermission;
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn("Notifications are not supported in this browser");
      return "denied";
    }

    if (Notification.permission === "granted") {
      return "granted";
    }

    if (Notification.permission === "denied") {
      return "denied";
    }

    try {
      const permission = await Notification.requestPermission();
      return permission as NotificationPermission;
    } catch (error) {
      console.error("Failed to request notification permission", error);
      return "denied";
    }
  }

  /**
   * Show a notification if permission is granted
   */
  async showNotification(options: NotificationOptions): Promise<boolean> {
    if (!this.isSupported) {
      console.warn("Notifications are not supported");
      return false;
    }

    // Auto-request permission if not yet determined
    if (Notification.permission === "default") {
      const permission = await this.requestPermission();
      if (permission !== "granted") {
        return false;
      }
    }

    if (Notification.permission !== "granted") {
      console.warn("Notification permission not granted");
      return false;
    }

    // Don't show notification if window is focused
    if (document.visibilityState === "visible" && document.hasFocus()) {
      return false;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || "/logo.png",
        badge: options.badge,
        tag: options.tag,
        requireInteraction: options.requireInteraction ?? false,
        silent: options.silent ?? false,
        data: options.data
      });

      // Auto-close after 6 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 6000);
      }

      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return true;
    } catch (error) {
      console.error("Failed to show notification", error);
      return false;
    }
  }

  /**
   * Show notification for new bookings
   */
  async notifyNewBookings(count: number): Promise<boolean> {
    return this.showNotification({
      title: "New Booking" + (count > 1 ? "s" : ""),
      body: `You have ${count} new booking${count > 1 ? "s" : ""} to review`,
      tag: "new-bookings",
      data: { type: "bookings", count }
    });
  }

  /**
   * Show notification for new chat messages
   */
  async notifyNewChats(count: number): Promise<boolean> {
    return this.showNotification({
      title: "New Chat Message" + (count > 1 ? "s" : ""),
      body: `You have ${count} unread chat message${count > 1 ? "s" : ""}`,
      tag: "new-chats",
      data: { type: "chats", count }
    });
  }

  /**
   * Show notification for new users
   */
  async notifyNewUsers(count: number): Promise<boolean> {
    return this.showNotification({
      title: "New User" + (count > 1 ? "s" : ""),
      body: `${count} new user${count > 1 ? "s have" : " has"} signed up`,
      tag: "new-users",
      data: { type: "users", count }
    });
  }

  /**
   * Show notification for robot alerts
   */
  async notifyRobotAlerts(count: number): Promise<boolean> {
    return this.showNotification({
      title: "Robot Alert" + (count > 1 ? "s" : ""),
      body: `${count} robot${count > 1 ? "s need" : " needs"} attention`,
      tag: "robot-alerts",
      data: { type: "robots", count }
    });
  }
}

export const browserNotificationService = new BrowserNotificationService();
